import React, { useState } from 'react';
import Modal from './ui/Modal';
import Button from './ui/Button';
import { HiUser, HiCheck, HiMagnifyingGlass } from 'react-icons/hi2';

function TeacherSelectionModal({
    isOpen,
    onClose,
    teachers = [],
    selectedTeacher = '',
    onTeacherSelect
}) {
    const [searchTerm, setSearchTerm] = useState('');
    const [localSelectedTeacher, setLocalSelectedTeacher] = useState(selectedTeacher);

    const handleTeacherSelect = (teacherName) => {
        setLocalSelectedTeacher(teacherName);
    };

    const handleConfirm = () => {
        onTeacherSelect(localSelectedTeacher);
        onClose();
    };

    const handleCancel = () => {
        setLocalSelectedTeacher(selectedTeacher); // Reset to original selection
        onClose();
    };

    // Ensure teachers is an array and filter out empty values
    const teacherOptions = Array.isArray(teachers) ? teachers.filter(t => t) : [];

    // Filter teachers based on search term
    const filteredTeachers = teacherOptions.filter(teacher => {
        const teacherName = typeof teacher === 'string' ? teacher : (teacher?.name || 'Unknown');
        return teacherName.toLowerCase().includes(searchTerm.toLowerCase());
    });

    // Sort teachers alphabetically
    const sortedTeachers = filteredTeachers.sort((a, b) => {
        const nameA = typeof a === 'string' ? a : (a?.name || '');
        const nameB = typeof b === 'string' ? b : (b?.name || '');
        return nameA.localeCompare(nameB);
    });

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleCancel}
            title="Select Teacher"
            className="max-w-md"
        >
            <div className="space-y-4">
                <div className="flex items-center gap-2 text-gray-600 mb-4">
                    <HiUser className="h-5 w-5" />
                    <span className="text-sm">Choose a teacher to see their schedule</span>
                </div>

                {/* Search input */}
                <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <HiMagnifyingGlass className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                        type="text"
                        placeholder="Search teachers..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>

                <div className="max-h-64 overflow-y-auto space-y-2">
                    {sortedTeachers.length > 0 ? (
                        sortedTeachers.map((teacher) => {
                            const teacherName = typeof teacher === 'string' ? teacher : (teacher?.name || 'Unknown');
                            const teacherKey = typeof teacher === 'string' ? teacher : (teacher?.id || teacher?.name || 'Unknown');
                            const isSelected = localSelectedTeacher === teacherName;

                            return (
                                <button
                                    key={teacherKey}
                                    onClick={() => handleTeacherSelect(teacherName)}
                                    className={`w-full text-left p-3 rounded-lg border-2 transition-all ${isSelected
                                            ? 'border-blue-500 bg-blue-50 text-blue-900'
                                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                        }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="font-medium">{teacherName}</span>
                                        {isSelected && (
                                            <HiCheck className="h-5 w-5 text-blue-600" />
                                        )}
                                    </div>
                                </button>
                            );
                        })
                    ) : (
                        <div className="text-center py-8 text-gray-500">
                            <HiUser className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                            <p>{searchTerm ? 'No teachers found matching your search' : 'No teachers available'}</p>
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button variant="secondary" onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleConfirm}
                        disabled={!localSelectedTeacher}
                    >
                        Select Teacher
                    </Button>
                </div>
            </div>
        </Modal>
    );
}

export default TeacherSelectionModal;
